import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import PersonIcon from '@mui/icons-material/Person';
import InventoryIcon from "@mui/icons-material/Inventory";
import NewspaperIcon from '@mui/icons-material/Newspaper';
import ApartmentIcon from '@mui/icons-material/Apartment';
import useScreenWidth from '../../../store/useScreenWidth';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { EndPoints } from '../../../utils/apiEndpoints';
import { useUserStore } from '../../../store/useUserStore';
import { darkModeBackgroud } from '../../../utils/constants';
import { useExpandNavMenu } from '../../../store/useExpandNavMenu';
import { useDarkModeStore } from '../../../store/useDarkModeStore';
import { useLanguageStore } from '../../../store/useLanguageStore';

const IconWrapper = styled('div')(() => ({
    position: 'relative',
    display: 'inline-block',
    width: 'fit-content',
}));

const PlusIcon = styled('div')(() => ({
    position: 'absolute',
    bottom: 12,
    right: -3,
    fontSize: 'large',
    color: '#32475C99',
    transform: 'translate(50%, 50%)',
}));

interface ShortCutInterface {
    id: number,
    icon: JSX.Element;
    title: string;
    subTitle: string;
    route: string;
    role: string;
}

const styleCss = { width: '22px', height: '22px' };

const ShortcutsBtn = () => {
    const navigate = useNavigate();
    const isSmallScreen = useScreenWidth();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { user } = useUserStore();
    const { language } = useLanguageStore();
    const { darkMode } = useDarkModeStore();
    const { isCollapsed } = useExpandNavMenu();

    const open = Boolean(anchorEl);
    const { ADDTICKET, ADDASSET, ADDINCIDENT, ADDUSER, ADDPURCHASEORDER, COMPANYNEWS, COMPANY } = EndPoints;
    const isAdminRole = user?.["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] === 'Admin';

    const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const IconWithPlus = ({ IconComponent, isAddItem = true }: { IconComponent: React.ElementType, isAddItem?: boolean }) => {
        return (
            <IconWrapper>
                <IconComponent sx={styleCss} />
                {isAddItem && <PlusIcon>+</PlusIcon>}
            </IconWrapper>
        );
    };


    const shortcuts: ShortCutInterface[] = [
        { id: 1, icon: <IconWithPlus IconComponent={NewspaperIcon} isAddItem={false} />, title: 'CompanyNews', subTitle: 'ManageCompanyNews', route: COMPANYNEWS, role: 'Admin' },
        { id: 2, icon: <IconWithPlus IconComponent={ApartmentIcon} />, title: 'Company', subTitle: 'CreateCompany', route: COMPANY, role: 'Admin' },
        { id: 3, icon: <IconWithPlus IconComponent={ConfirmationNumberIcon} />, title: 'AddTicket', subTitle: 'CreateNewTicket', route: ADDTICKET, role: 'Admin' },
        { id: 4, icon: <IconWithPlus IconComponent={InventoryIcon} />, title: 'AddAsset', subTitle: 'CreateNewAsset', route: ADDASSET, role: 'Admin' },
        { id: 5, icon: <IconWithPlus IconComponent={BuildOutlinedIcon} />, title: 'AddInvoice', subTitle: 'CreateNewInvoice', route: ADDINCIDENT, role: 'Employee' },
        { id: 6, icon: <IconWithPlus IconComponent={ShoppingCartOutlinedIcon} />, title: 'AddPurchaseOrder', subTitle: 'CreatePurchaseOrder', route: ADDPURCHASEORDER, role: 'Employee' },
        { id: 7, icon: <IconWithPlus IconComponent={PersonIcon} />, title: 'AddUser', subTitle: 'CreateNewUser', route: ADDUSER, role: 'Admin' },
    ];

    const handlePosition = () => {
        if (isSmallScreen) return '-100px';
        else return isCollapsed ? '-320px' : '-170px';
    }

    const handleNavigate = (route: string) => {
        navigate(route);
        handleClose();
    }

    return (
        <React.Fragment>
            <Button
                className="button-hover-circle"
                variant="text"
                onClick={handleClick}
                title='Shortcuts'
            >
                <GridViewOutlinedIcon
                    className='icon-sidebar-top-color'
                    sx={{ width: '22px', height: '22px', color: darkMode ? 'white' : '#4D5F71' }}
                />
            </Button>
            <Popover
                id="popover"
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                sx={{
                    '& .MuiPopover-paper': {
                        marginLeft: handlePosition(),
                    },
                }}
            >
                <div
                    className='d-flex flex-column'
                    style={{ width: isSmallScreen ? '200px' : '400px', color: '#32475C99', background: darkMode ? darkModeBackgroud : 'white' }}
                >
                    <div
                        className='p-3 d-flex justify-content-between'
                        style={{ borderBottom: '1px solid rgba(50, 71, 92, 0.12)' }}
                    >
                        <span style={{ fontSize: '18px', fontWeight: 600, color: darkMode ? 'white' : 'inherit' }}>Shortcuts</span>
                    </div>
                    <div
                        className={`shortcuts-grid ${isSmallScreen && 'shortcuts-grid-column-1fr'}`}
                        style={{ maxHeight: '450px', overflowY: 'auto' }}
                    >
                        {
                            shortcuts?.filter(s => isAdminRole ? s : s.role === 'Employee').map((item, index) =>
                                <div
                                    key={item.id}
                                    className={`shortcuts-grid-item ${darkMode && 'shortcuts-grid-item-dark-mode'}`}
                                    style={index % 2 !== 0 ? { borderBottom: '1px solid rgba(50, 71, 92, 0.12)' } : {}}
                                    role='button'
                                    onClick={() => handleNavigate(item.route)}
                                    title={language[item.subTitle] || item.subTitle}
                                >
                                    <div className='d-flex flex-column align-items-center justify-content-center'>
                                        <span className='p-3 rounded-circle mb-1' style={{ background: '#EBEDF0' }}>{item.icon}</span>
                                        <span className="fw-bold" style={{ color: darkMode ? 'white' : 'rgba(50, 71, 92, 0.6)', fontSize: '18px', opacity: '.9' }}>{language[item.title] || item.title}</span>
                                        <div
                                            className='text-truncate'
                                            style={{
                                                fontSize: '.875rem',
                                                color: darkMode ? 'white' : 'rgba(50, 71, 92, 0.38)',
                                                fontWeight: 500,
                                                maxWidth: 150
                                            }}
                                        >
                                            {language[item.subTitle] || item.subTitle}
                                        </div>
                                    </div>
                                </div>
                            )
                        }
                    </div>
                </div>
            </Popover>
        </React.Fragment>
    );
};

export default ShortcutsBtn;
